import { useState, useEffect } from 'react';
import { supabase, supabaseUrl, supabaseAnonKey } from '../commons/config/settings';
import { toast } from 'react-hot-toast';

export interface Profile {
  id: string;
  full_name: string;
  email: string;
  role: 'admin' | 'agent';
  disabled: boolean;
  created_at: string;
  updated_at: string;
  email_confirmed_at?: string | null;
}

interface ProfileWithAuth {
  id: string;
  full_name: string;
  email: string;
  role: string;
  disabled: boolean;
  created_at: string;
  updated_at: string;
  email_confirmed_at: string | null;
}

export const useProfiles = () => {
  const [profiles, setProfiles] = useState<Profile[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchProfiles = async () => {
    try {
      setLoading(true);
      setError(null);

      // Recupera i profili con i dati di verifica email usando RPC function
      const { data: profilesData, error: profilesError } = await supabase
        .rpc('get_profiles_with_auth_status');

      if (profilesError) {
        throw profilesError;
      }

      // Trasforma i dati nel formato Profile
      const profilesWithAuth: Profile[] = (profilesData || []).map((profile: ProfileWithAuth) => ({
        id: profile.id,
        full_name: profile.full_name,
        email: profile.email,
        role: profile.role as 'admin' | 'agent',
        disabled: profile.disabled,
        created_at: profile.created_at,
        updated_at: profile.updated_at,
        email_confirmed_at: profile.email_confirmed_at,
      }));

      setProfiles(profilesWithAuth);
    } catch (err) {
      console.error('Error fetching profiles:', err);
      setError(err instanceof Error ? err.message : 'Errore nel caricamento dei profili');
    } finally {
      setLoading(false);
    }
  };

    const optimisticUpdateProfiles = (id: string, updates: Partial<Profile>) => {
    setProfiles(prev => 
      prev.map(profile => 
        profile.id === id ? { ...profile, ...updates } : profile
      )
    );
  };

  const deleteProfile = async (id: string): Promise<{ success: boolean; error?: string }> => {
    // Save backup for rollback
    const prevProfiles = [...profiles];
    const profileToDelete = profiles.find(p => p.id === id);

    if (!profileToDelete) {
      return {
        success: false,
        error: 'Profilo non trovato'
      };
    }

    try {
      // Verifica che l'utente corrente sia admin e recupera la session
      const { data: { user } } = await supabase.auth.getUser();
      const userRole = user?.user_metadata?.role || user?.app_metadata?.role;

      if (userRole !== 'admin') {
        throw new Error('Solo gli amministratori possono eliminare utenti');
      }

      // Get current session for Edge Function call
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('Sessione non valida');
      }

      // Optimistic update: remove immediately from UI
      setProfiles(prev => prev.filter(profile => profile.id !== id));

      // Prima elimina il profilo dalla tabella profiles
      const { error: profileError } = await supabase
        .from('profiles')
        .delete()
        .eq('id', id);

      if (profileError) {
        throw new Error(`Errore nell'eliminazione del profilo: ${profileError.message}`);
      }

      // Poi chiama l'Edge Function per eliminare l'utente da auth.users
      const response = await fetch(`${supabaseUrl}/functions/v1/delete-user`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
          'apikey': supabaseAnonKey
        },
        body: JSON.stringify({ userId: id })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(`Errore nell'eliminazione dell'utente: ${errorData.error || response.statusText}`);
      }

      // Success - show toast
      toast.success(`Utente "${profileToDelete.full_name}" eliminato con successo`);
      return { success: true };

    } catch (err) {
      // Rollback on error
      setProfiles(prevProfiles);

      const errorMessage = err instanceof Error ? err.message : 'Errore nell\'eliminazione del profilo';
      toast.error(`Impossibile eliminare l'utente. ${errorMessage}`);

      console.error('Error deleting profile:', err);
      return {
        success: false,
        error: errorMessage
      };
    }
  };

  const updateProfile = async (id: string, updates: Partial<Profile>) => {
    try {
      const { error: updateError } = await supabase
        .from('profiles')
        .update(updates)
        .eq('id', id);

      if (updateError) {
        throw updateError;
      }

      // Refresh the profiles list
      await fetchProfiles();
      return { success: true };
    } catch (err) {
      console.error('Error updating profile:', err);
      return { 
        success: false, 
        error: err instanceof Error ? err.message : 'Errore nell\'aggiornamento del profilo' 
      };
    }
  };

  const createProfile = async (profile: Omit<Profile, 'id' | 'created_at' | 'updated_at'>) => {
    try {
      const { error: createError } = await supabase
        .from('profiles')
        .insert([profile]);

      if (createError) {
        throw createError;
      }

      // Refresh the profiles list
      await fetchProfiles();
      return { success: true };
    } catch (err) {
      console.error('Error creating profile:', err);
      return { 
        success: false, 
        error: err instanceof Error ? err.message : 'Errore nella creazione del profilo' 
      };
    }
  };

  useEffect(() => {
    fetchProfiles();
  }, []);

  return {
    profiles,
    loading,
    error,
    fetchProfiles,
    deleteProfile,
    updateProfile,
    createProfile,
    optimisticUpdateProfiles,
  };
}; 